import type { ReactNode } from 'react';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '../../lib/cn';
import { Z, type ZIndex } from '../../tokens/z-index';

// ---- Root ----
export interface AlertDialogProps extends AlertDialogPrimitive.AlertDialogProps {
  children: ReactNode;
}

export function AlertDialog({ children, ...rest }: AlertDialogProps) {
  return <AlertDialogPrimitive.Root {...rest}>{children}</AlertDialogPrimitive.Root>;
}

// ---- Trigger ----
export interface AlertDialogTriggerProps extends AlertDialogPrimitive.AlertDialogTriggerProps {}

export function AlertDialogTrigger({ children, ...rest }: AlertDialogTriggerProps) {
  return (
    <AlertDialogPrimitive.Trigger asChild {...rest}>
      {children}
    </AlertDialogPrimitive.Trigger>
  );
}

// ---- Content ----
export interface AlertDialogContentProps extends AlertDialogPrimitive.AlertDialogContentProps {
  /**
   * Override do tier de z-index. Default: `Z.MODAL` (400). Usar
   * `Z.TOAST` (600) quando o ConfirmDialog/AlertDialog abre por cima de um
   * Sheet hospedado em `Z.DROPDOWN_OVER_MODAL` (500) — senão o overlay
   * compartilha tier com o host e fica atrás dele (stacking por ordem de mount).
   */
  zIndex?: ZIndex;
}

export function AlertDialogContent({
  children,
  className,
  style,
  zIndex = Z.MODAL,
  ...rest
}: AlertDialogContentProps) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay
        style={{ zIndex }}
        className={cn(
          // Tier default Z.MODAL (400) — same tier as Dialog so two modals in
          // sequence stack consistently (Radix handles its own internal ordering).
          // backdrop-blur-sm = 4px (sutil — Linear-style polish W5)
          'fixed inset-0 flex items-center justify-center bg-secondary-950/50 backdrop-blur-sm p-4',
          'animate-in fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        )}
      />
      <AlertDialogPrimitive.Content
        style={{ zIndex, ...style }}
        className={cn(
          'fixed inset-0 m-auto flex h-fit max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-lg',
          'flex-col overflow-y-auto rounded-lg border border-slate-200 bg-white p-6 shadow-lg scrollbar-subtle',
          'animate-in fade-in-0 zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...rest}
      >
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
}

// ---- Header ----
export function AlertDialogHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('flex flex-col gap-1.5', className)}>{children}</div>;
}

// ---- Title ----
export interface AlertDialogTitleProps extends AlertDialogPrimitive.AlertDialogTitleProps {}

export function AlertDialogTitle({ children, className, ...rest }: AlertDialogTitleProps) {
  return (
    <AlertDialogPrimitive.Title
      className={cn('text-lg font-heading font-semibold text-secondary-950', className)}
      {...rest}
    >
      {children}
    </AlertDialogPrimitive.Title>
  );
}

// ---- Description ----
export interface AlertDialogDescriptionProps
  extends AlertDialogPrimitive.AlertDialogDescriptionProps {}

export function AlertDialogDescription({
  children,
  className,
  ...rest
}: AlertDialogDescriptionProps) {
  return (
    <AlertDialogPrimitive.Description
      className={cn('text-sm font-body text-slate-500', className)}
      {...rest}
    >
      {children}
    </AlertDialogPrimitive.Description>
  );
}

// ---- Footer ----
export function AlertDialogFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mt-6 flex items-center justify-end gap-3', className)}>{children}</div>
  );
}

// ---- Action (confirm) ----
export interface AlertDialogActionProps extends AlertDialogPrimitive.AlertDialogActionProps {}

export function AlertDialogAction({ children, ...rest }: AlertDialogActionProps) {
  return (
    <AlertDialogPrimitive.Action asChild {...rest}>
      {children}
    </AlertDialogPrimitive.Action>
  );
}

// ---- Cancel ----
export interface AlertDialogCancelProps extends AlertDialogPrimitive.AlertDialogCancelProps {}

export function AlertDialogCancel({ children, ...rest }: AlertDialogCancelProps) {
  return (
    <AlertDialogPrimitive.Cancel asChild {...rest}>
      {children}
    </AlertDialogPrimitive.Cancel>
  );
}
